import { TimerInfoState } from "../../timer-controller";
import { TimerDto } from "../../../api/tournament";
import { StructuresResponse } from "../../tournament/hook/useEditTournament";

function formatRemainingTime(minutes: number, seconds: number): string {
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(seconds).padStart(2, "0");
  return `${formattedMinutes}:${formattedSeconds}`;
}

function formatMillisecondsToTime(ms: number): string {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);

  if (+minutes > 99) {
    if (+hours > 99) {
      const days = String(Math.floor(ms / 86400000)).padStart(2, "0");
      const formattedHours = String(hours % 24).padStart(2, "0");
      return `${days}:${formattedHours}`;
    }

    const formattedHours = String(hours % 24).padStart(2, "0");
    const formattedMinutes = String(minutes % 60).padStart(2, "0");
    return `${formattedHours}:${formattedMinutes}`;
  }

  return formatRemainingTime(minutes, seconds);
}

function formatRemainingTimeWithHour(
  hour: number,
  minutes: number,
  seconds: number,
): string {
  const formattedHours = String(hour).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(seconds).padStart(2, "0");
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

function formatElapsedTime(totalMilliseconds: number): string {
  if (totalMilliseconds < 0) return "00:00:00";
  const totalSeconds = Math.floor(totalMilliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const formattedHours = String(hours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(seconds).padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export function calculateHomeLevel(
  elapsedTimeMs: number,
  levels: StructuresResponse[],
) {
  let accumulatedTime = 0;
  let currentLevel = null;
  let previousLevel = null; // 이전 LEVEL을 추적하기 위한 변수
  let remainingTimeInCurrentLevelMs = 0;
  let timeUntilNextBreakMs: number | null = null;
  let breakFound = false;

  for (let i = 0; i < levels.length; i++) {
    const level = levels[i];
    const levelTimeMs = level.time * 60000;

    accumulatedTime += levelTimeMs;

    // 이전 레벨이 "LEVEL" 타입이면 추적
    if (level.type !== "BREAK") {
      previousLevel = level;
    }

    // 현재 레벨 설정
    if (elapsedTimeMs < accumulatedTime && currentLevel === null) {
      currentLevel = level;
      remainingTimeInCurrentLevelMs = accumulatedTime - elapsedTimeMs;
    }

    // 다음 BREAK까지 남은 시간 계산
    if (currentLevel && level.type === "BREAK") {
      if (!breakFound) {
        timeUntilNextBreakMs = accumulatedTime - elapsedTimeMs - levelTimeMs;
        breakFound = true;

        // 현재 레벨이 BREAK일 때 이전 LEVEL로 설정
        if (currentLevel.type === "BREAK" && previousLevel !== null) {
          currentLevel = previousLevel;
        }
      }
    }
  }

  // 모든 레벨을 초과한 경우, 마지막 레벨로 설정
  if (currentLevel === null && levels.length > 0) {
    currentLevel = levels[levels.length - 1];
    remainingTimeInCurrentLevelMs = 0;
  }

  return {
    currentLevel,
    remainingTimeInCurrentLevelMs,
    timeUntilNextBreakMs,
  };
}

export function controlLevel(
  count: number,
  levels: StructuresResponse[],
  current_level: StructuresResponse,
): number {
  if (!current_level.ord) return -1;
  if (count < 0 && current_level.ord === 1) return -1;
  if (count > 0 && current_level.ord === levels.length) return 1;

  const result_level = levels[current_level.ord - 1 + count];
  if (!result_level.ord) return -1;
  let playTime = 0;
  for (let i = 0; i < result_level.ord - 1; i++) {
    playTime += levels[i].time * 60000;
  }

  return playTime;
}

export function calculateLevel(
  elapsedTimeMs: number,
  levels: StructuresResponse[],
) {
  let accumulatedTime = 0;
  let currentLevel: StructuresResponse | null = null;
  let remainingTimeInCurrentLevelMs = 0;
  let elapsedTimeInCurrentLevelMs = 0;
  let timeUntilNextBreakMs: number | null = null;
  let accumulatedTimeForBreak = 0;

  for (let i = 0; i < levels.length; i++) {
    const level = levels[i];
    const levelTimeMs = level.time * 60000;
    const levelStartTime = accumulatedTime;
    const levelEndTime = accumulatedTime + levelTimeMs;

    if (currentLevel === null) {
      accumulatedTimeForBreak += levelTimeMs;
    }

    // 시작 전일 때 시작까지 남은 시간 계산
    if (elapsedTimeMs < 0 && i === 0) {
      currentLevel = level;
      remainingTimeInCurrentLevelMs = -elapsedTimeMs;
      elapsedTimeInCurrentLevelMs = -elapsedTimeMs;
    }

    // 시작 후 레벨에 해당 되면 계산
    if (elapsedTimeMs >= levelStartTime && elapsedTimeMs < levelEndTime) {
      currentLevel = level;
      remainingTimeInCurrentLevelMs = levelEndTime - elapsedTimeMs;
      elapsedTimeInCurrentLevelMs = elapsedTimeMs - levelStartTime;
    }

    // 다음 BREAK까지 남은 시간 계산
    if (
      level.type === "BREAK" &&
      levelStartTime > elapsedTimeMs &&
      timeUntilNextBreakMs === null
    ) {
      timeUntilNextBreakMs = levelStartTime - elapsedTimeMs;
    }

    accumulatedTime += levelTimeMs;
  }

  // 현재 레벨이 BREAK인 경우 다음 BREAK까지 재 계산
  if (currentLevel && currentLevel.type === "BREAK") {
    const currentBreakEndTime =
      accumulatedTimeForBreak - currentLevel.time * 60000;

    for (let i = 0; i < levels.length; i++) {
      const level = levels[i];
      const levelStartTime =
        i === 0
          ? 0
          : levels.slice(0, i).reduce((acc, l) => acc + l.time * 60000, 0);
      if (level.type === "BREAK" && levelStartTime > currentBreakEndTime) {
        timeUntilNextBreakMs = levelStartTime - elapsedTimeMs;
        break;
      }
    }
  }

  if (timeUntilNextBreakMs !== null && timeUntilNextBreakMs <= 0) {
    timeUntilNextBreakMs = 0;
  }

  return {
    currentLevel,
    remainingTimeInCurrentLevelMs,
    elapsedTimeInCurrentLevelMs,
    timeUntilNextBreakMs,
  };
}

function formatMilliseconds(ms: number): string {
  if (ms < 0) {
    ms = 0;
  }

  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const minutesStr = minutes.toString().padStart(2, "0");
  const secondsStr = seconds.toString().padStart(2, "0");

  return `${minutesStr}분${secondsStr}초`;
}

export function calculateTimes(
  timerData: TimerDto,
  levels: StructuresResponse[],
  diffTime?: number | null,
): TimerInfoState {
  let now = new Date();
  if (diffTime) {
    now = new Date(now.getTime() - diffTime);
  }
  const updatedAt = new Date(timerData.updatedAt);

  let elapsedTimeMs;

  if (timerData.isStop) {
    elapsedTimeMs = timerData.playTime;
  } else {
    elapsedTimeMs = now.getTime() - updatedAt.getTime() + timerData.playTime;
  }

  if (elapsedTimeMs === 0) {
    return {
      elapsedTimeMs,
      remainingTimeInCurrentLevelMs: 0,
      elapsedTime: "00:00:00",
      currentLevel: levels[0],
      remainingTimeInCurrentLevel: "00:00",
      timeUntilNextBreak: "00:00:00",
      elapsedTimeInCurrentLevel: "00분00초",
      elapsedTimeInCurrentLevelMs: 0,
    };
  }

  const {
    currentLevel,
    remainingTimeInCurrentLevelMs,
    elapsedTimeInCurrentLevelMs,
    timeUntilNextBreakMs,
  } = calculateLevel(elapsedTimeMs, levels);

  let nextBreakSeconds;
  let nextBreakMinutes;
  let nextBreakHours;
  if (timeUntilNextBreakMs) {
    nextBreakHours = Math.floor(timeUntilNextBreakMs / 3600000);
    const remainingMsAfterHours = timeUntilNextBreakMs % 3600000;

    nextBreakMinutes = Math.floor(remainingMsAfterHours / 60000);
    nextBreakSeconds = Math.floor((remainingMsAfterHours % 60000) / 1000);
  } else {
    nextBreakHours = 0;
    nextBreakMinutes = 0;
    nextBreakSeconds = 0;
  }

  const elapsedTimeInCurrentLevel = formatMilliseconds(
    elapsedTimeInCurrentLevelMs,
  );

  return {
    elapsedTimeMs,
    elapsedTime: formatElapsedTime(elapsedTimeMs),
    currentLevel,
    remainingTimeInCurrentLevel: formatMillisecondsToTime(
      remainingTimeInCurrentLevelMs,
    ),
    timeUntilNextBreak: timeUntilNextBreakMs
      ? formatRemainingTimeWithHour(
          nextBreakHours,
          nextBreakMinutes,
          nextBreakSeconds,
        )
      : "00:00:00",
    elapsedTimeInCurrentLevel,
    elapsedTimeInCurrentLevelMs,
    remainingTimeInCurrentLevelMs,
  };
}

export function getPreviousLevel(
  currentLevel: StructuresResponse,
  levels: StructuresResponse[],
): StructuresResponse | null {
  if (!currentLevel.ord || currentLevel.ord <= 1) return null;
  return levels[currentLevel.ord - 2];
}

export function getPreviousLevelDurationMs(
  currentLevel: StructuresResponse,
  levels: StructuresResponse[],
): number {
  const previousLevel = getPreviousLevel(currentLevel, levels);
  if (!previousLevel) return 0;
  return previousLevel.time * 60000; // Convert minutes to milliseconds
}
